import BookshelfIcon from "@assets/compass-sm.png";
import AnnotationsMenu from "@assets/highlight-unselected.png";
import NavMenuIconOpen from "@assets/nav-menu-icon-open.svg";
import ReadingSettingsIcon from "@assets/reading-settings.svg";
import NavMenuIcon from "@assets/nav-menu-icon.svg";
import styled from "@emotion/styled";
import { css, useMediaQuery, useTheme } from "@mui/material";
import Slide from "@mui/material/Slide";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { Link, Outlet } from "react-router";
import { pxToRem } from "../../design/utils";
import { BookmarkButton } from "../bits/bookmarks/BookmarkButton";
import { IconButton } from "../bits/buttons";
import {
  FlexColContainer,
  FlexRowContainer,
  SeparatorLine,
} from "../bits/layout";
import { responsiveMargins } from "../bits/layout/cssHelpers";
import { GridItem, GridWrapper } from "../bits/layout/Grid";
import { Header } from "../bits/layout/Header";
import { ArticleNavPanel } from "../patterns/ArticleNavPanel";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { SettingsContext } from "../contexts/WithSettings/SettingsContext";
import { ArticleContext } from "../contexts/WithArticle/ArticleContext";
import {
  SidebarContext,
  SidebarState,
} from "../contexts/WithSidebars/SidebarContext";
import { SettingsMenu } from "../patterns/SettingsMenu";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";

const EReaderViewWrapper = styled(GridWrapper)`
  font-family: "Literata", sans-serif;
  ${({ theme }) => theme.layers.relief.accent_20.noShadow};
`;

const EReaderHeaderLabel = styled(GridItem)`
  ${responsiveMargins}
  position: relative;
  ${({ theme }) => theme.customTypography.label._1};
  color: ${({ theme }) => theme.colours.accent.dark._30};
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    padding-inline: 0 !important;
    padding-left: 0 !important;
  }
`;

const EReaderHeaderText = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EReaderHeaderTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${pxToRem(6)};
  max-width: calc(100% + ${pxToRem(30)});
  min-width: 0;
  position: relative;
`;

export const ArticleEReaderView = () => {
  const {
    currentSection,
    setScrollDisabled,
    title,
    articleSlug,
    contents,
    ghostPost,
  } = useContext(ArticleContext);
  const { settingsOpen, setSettingsOpen } = useContext(SettingsContext);
  const { annotationsOpen, setAnnotationsOpen } = useContext(AnnotationContext);
  const theme = useTheme();
  const isNarrow = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.sm}px)`,
  );
  const isMedium = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`,
  );

  const { sidebarState, setSidebarState, toggleSidebarState } =
    useContext(SidebarContext);

  const toggleMenu = useCallback(
    (sidebarItem: SidebarState) => {
      setScrollDisabled(true);
      toggleSidebarState(sidebarItem);
      if (currentSection) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            const el = document.getElementById(currentSection.id);
            el?.scrollIntoView({ behavior: "instant", block: "start" });
            setScrollDisabled(false);
          });
        }, 300);
      }
    },
    [currentSection, setScrollDisabled, toggleSidebarState],
  );

  useEffect(() => {
    setSidebarState(SidebarState.NAV);
  }, [setSidebarState]);

  const { open, navOpen } = useMemo(() => {
    const open = sidebarState !== SidebarState.CLOSED;
    return {
      open,
      navOpen: sidebarState === SidebarState.NAV,
    };
  }, [sidebarState]);

  const bookmarkButtonProps = {
    articleSlug,
    contents,
    sectionId: currentSection?.id,
    sectionLabel: currentSection?.label,
    annotationsOpen: false,
  };

  return (
    <EReaderViewWrapper columns={12}>
      <Header ereader={true}>
        <GridItem span={4}>
          <FlexRowContainer
            style={{
              justifyContent: isMedium ? "flex-start" : "space-between",
              paddingRight: pxToRem(6),
            }}
          >
            <FlexRowContainer>
              <Link to="/">
                <IconButton
                  css={css`
                    width: 2rem;
                    padding: 0 0.25rem;
                  `}
                  src={BookshelfIcon}
                  alt="Compass"
                  title="Home"
                />
              </Link>
              <SeparatorLine />
              <IconButton
                onClick={() => toggleMenu(SidebarState.NAV)}
                active={navOpen}
                src={navOpen ? NavMenuIcon : NavMenuIconOpen}
                alt="Toggle menu"
                title="Toggle menu"
              />
              {!isMedium ? (
                <p
                  style={{
                    ...theme.customTypography.label._3,
                    maxHeight: pxToRem(33),
                    lineHeight: 1,
                    minWidth: pxToRem(160),
                    userSelect: "none",
                  }}
                >
                  {ghostPost?.issueName ?? title}
                </p>
              ) : (
                <SeparatorLine />
              )}
            </FlexRowContainer>
            <SeparatorLine />
            {isNarrow && (
              <div
                css={css`
                  align-items: center;
                  display: flex;
                  margin-right: ${pxToRem(8)};
                  min-width: ${pxToRem(34)};
                `}
              >
                <BookmarkButton {...bookmarkButtonProps} />
              </div>
            )}
          </FlexRowContainer>
        </GridItem>
        <EReaderHeaderLabel
          span={isMedium ? 6 : 7}
          style={{ userSelect: "none" }}
        >
          <EReaderHeaderTitleRow>
            {!isNarrow && (
              <div
                css={css`
                  align-items: center;
                  display: flex;
                  height: 100%;
                  left: -42px;
                  position: absolute;

                  @media (max-width: ${theme.breakpoints.values.lg}px) {
                    display: flex;
                    justify-content: flex-start;
                    min-width: ${pxToRem(34)};
                  }

                  @media (min-width: ${theme.breakpoints.values.lg + 1}px) {
                    display: flex;
                    height: 100%;
                    left: -42px;
                    position: absolute;
                  }
                `}
              >
                <BookmarkButton {...bookmarkButtonProps} />
              </div>
            )}
            <EReaderHeaderText>
              {!isNarrow ? currentSection?.label : ""}
            </EReaderHeaderText>
          </EReaderHeaderTitleRow>
        </EReaderHeaderLabel>
        <GridItem
          span={isMedium ? 2 : 1}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => {
              setScrollDisabled(true);
              setAnnotationsOpen(!annotationsOpen);
              if (currentSection) {
                requestAnimationFrame(() => {
                  const el = document.getElementById(currentSection.id);
                  el?.scrollIntoView({ behavior: "instant", block: "start" });
                  setScrollDisabled(false);
                });
              }
            }}
            src={AnnotationsMenu}
            title="Toggle annotations"
            alt="Annotations menu"
            active={annotationsOpen}
          />
          <SeparatorLine style={{ margin: "0 10px" }} />
          <IconButton
            onClick={() => setSettingsOpen(!settingsOpen)}
            active={settingsOpen}
            src={ReadingSettingsIcon}
            title="Toggle reading settings"
            alt="Settings menu"
          />
        </GridItem>
      </Header>
      <Slide direction="right" in={open}>
        <GridItem
          span={4}
          hidden={!open}
          css={({ breakpoints }) => css`
            user-select: none;
            height: calc(100vh - 2.75rem);
            height: calc(100dvh - 2.75rem);
            width: 100%;
            position: relative;
            z-index: 1;
            @media (max-width: ${breakpoints.values.md}px) {
              visibility: ${open ? "visible" : "hidden"};
            }
          `}
        >
          <div
            css={({ layers, breakpoints }) => css`
              height: 100%;
              display: flex;
              flex-direction: column;
              overflow-y: hidden;
              background: white;

              @media (max-width: ${breakpoints.values.md}px) {
                position: absolute;
                display: flex;
                top: 0;
                left: 0;
                z-index: 10;
                transform: ${open ? "translateX(0)" : "translateX(-100%)"};
                grid-column: unset;
                ${layers.relief.accent_20.noShadow};
                max-width: 400px;
              }
            `}
          >
            <ArticleNavPanel open={navOpen} />
            <div
              css={({ colours }) => css`
                display: flex;
                flex-direction: column;
                background-color: ${colours.accent.light._40};
                color: ${colours.accent.mid.dark15};
                font-family: Manrope;
                font-size: 18px;
                font-weight: 700;
                user-select: none;
              `}
            >
              {ghostPost?.tags && ghostPost.tags.length > 0 && (
                <>
                  <div
                    css={css`
                      height: 32px;
                      padding: 8px 12px;
                      font-size: 0.8em;
                    `}
                  >
                    EXPLORE TOPIC
                  </div>
                  <div
                    css={({ colours }) => css`
                      background-color: ${colours.accent.light._10};
                      color: ${colours.accent.dark._20};
                      padding: 8px 12px;
                      display: flex;
                      font-size: 0.8em;
                      flex-wrap: wrap;
                    `}
                  >
                    {ghostPost.tags
                      .filter((t) => t.name && !t.name.startsWith("#"))
                      .map((tag, index) => {
                        if (isMedium && index > 2) return null;

                        return (
                          <Link
                            key={tag.id}
                            to={`/frj/tag/${tag.slug}`}
                            css={css`
                              padding: 6px;
                              white-space: nowrap;
                            `}
                          >
                            {tag.name}
                            {tag.count?.posts != null &&
                              (tag.count.posts > 0
                                ? ` (${tag.count.posts})`
                                : "")}
                          </Link>
                        );
                      })}
                  </div>
                </>
              )}
              {ghostPost?.relatedPosts && ghostPost.relatedPosts.length > 0 && (
                <div>
                  <div
                    css={css`
                      height: 32px;
                      padding: 8px 12px;
                      font-size: 0.8em;
                    `}
                  >
                    RELATED
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    {ghostPost.relatedPosts.map((post, index) => {
                      if (isMedium && index > 0) return null;
                      return (
                        <Link
                          key={post.slug}
                          to={`/article/${post.slug}`}
                          css={({ colours }) => css`
                            display: flex;
                            flex-direction: column;
                            background-color: ${colours.accent.dark._20};
                            color: ${colours.accent.light._10};
                            padding: 8px 12px;
                            text-decoration: none;
                            width: 100%;
                            font-size: 0.8em;

                            border-bottom: 1px solid ${colours.accent.dark._10};
                            &:hover {
                              background: ${colours.accent.dark._30};
                            }
                          `}
                        >
                          <FlexRowContainer
                            css={css`
                              align-items: flex-start;
                            `}
                          >
                            <ArrowCircleRightIcon />
                            <FlexColContainer
                              css={css`
                                width: 100%;
                              `}
                            >
                              {post.title}
                              {post.authors.length > 0 && (
                                <span
                                  css={({ colours }) => css`
                                    font-size: 0.7em;
                                    font-weight: 400;
                                    opacity: 0.75;
                                    color: ${colours.accent.light._40};
                                  `}
                                >
                                  {post.authors
                                    .map((a) => a.name)
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                              )}
                            </FlexColContainer>
                          </FlexRowContainer>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* don't show next article if we are viewing an issue */}
              {ghostPost?.nextPost &&
                !ghostPost.issueArticles?.length &&
                !isNarrow && (
                  <div>
                    <div
                      css={css`
                        height: 32px;
                        padding: 8px 12px;
                        font-size: 0.8em;
                      `}
                    >
                      NEXT ARTICLE
                    </div>
                    <Link
                      to={`/article/${ghostPost.nextPost.slug}`}
                      css={({ colours }) => css`
                        display: flex;
                        flex-direction: column;
                        background-color: ${colours.accent.dark._20};
                        color: ${colours.accent.light._10};
                        text-decoration: none;
                        padding: 8px 12px;
                        overflow: hidden;
                        width: 100%;
                        font-size: 0.8em;
                        &:hover {
                          background: ${colours.accent.dark._30};
                        }
                      `}
                    >
                      <FlexRowContainer
                        css={css`
                          align-items: flex-start;
                        `}
                      >
                        <ArrowCircleRightIcon />
                        <FlexColContainer
                          css={css`
                            width: 100%;
                          `}
                        >
                          {ghostPost.nextPost.title}
                          {ghostPost.nextPost.authors.length > 0 && (
                            <span
                              css={({ colours }) => css`
                                font-size: 0.7em;
                                font-weight: 400;
                                opacity: 0.75;
                                color: ${colours.accent.light._40};
                              `}
                            >
                              {ghostPost.nextPost.authors
                                .map((a) => a.name)
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          )}
                        </FlexColContainer>
                      </FlexRowContainer>
                    </Link>
                  </div>
                )}
            </div>
          </div>
        </GridItem>
      </Slide>
      <GridItem
        span={open ? 8 : 12}
        id="ereader-content"
        css={css`
          height: calc(100vh - ${pxToRem(44)});
          height: calc(100dvh - ${pxToRem(44)});
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
        `}
      >
        <Outlet />
        {ghostPost?.authors && ghostPost.authors.length > 0 && (
          <div
            css={({ colours }) => css`
              border-top: 1px solid ${colours.accent.light._30};
              display: flex;
              flex-direction: column;
              user-select: none;
              gap: ${pxToRem(16)};
              margin: ${pxToRem(48)} auto;
              max-width: ${pxToRem(680)};
              padding: ${pxToRem(32)} ${pxToRem(24)};
            `}
          >
            {ghostPost.authors.map((author) => (
              <div
                key={author.id ?? author.name}
                css={css`
                  align-items: center;
                  display: flex;
                  gap: ${pxToRem(16)};
                `}
              >
                {author.profile_image ? (
                  <img
                    src={author.profile_image}
                    alt={author.name ?? "Author"}
                    css={css`
                      border-radius: 50%;
                      flex-shrink: 0;
                      height: ${pxToRem(56)};
                      object-fit: cover;
                      width: ${pxToRem(56)};
                    `}
                  />
                ) : (
                  <div
                    css={({ colours }) => css`
                      align-items: center;
                      background-color: ${colours.accent.light._30};
                      border-radius: 50%;
                      display: flex;
                      flex-shrink: 0;
                      font-family: Manrope;
                      font-size: ${pxToRem(20)};
                      font-weight: 700;
                      height: ${pxToRem(56)};
                      justify-content: center;
                      width: ${pxToRem(56)};
                    `}
                  >
                    {author.name?.charAt(0) ?? "?"}
                  </div>
                )}
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: ${pxToRem(4)};
                  `}
                >
                  <span
                    css={({ colours }) => css`
                      color: ${colours.accent.dark._30};
                      font-family: Manrope;
                      font-size: ${pxToRem(16)};
                      font-weight: 700;
                    `}
                  >
                    {author.name}
                  </span>
                  {author.bio && (
                    <span
                      css={({ colours }) => css`
                        color: ${colours.accent.mid.dark15};
                        font-family: Manrope;
                        font-size: ${pxToRem(14)};
                        font-weight: 400;
                      `}
                    >
                      {author.bio}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </GridItem>
      <Slide
        direction="left"
        in={settingsOpen}
        css={css`
          height: calc(100vh - ${pxToRem(44)});
          height: calc(100dvh - ${pxToRem(44)});
          overflow-y: auto;
        `}
      >
        <SettingsMenu style={{ display: settingsOpen ? "block" : "none" }} />
      </Slide>
    </EReaderViewWrapper>
  );
};
